/**
 * Usage → Predictive alerts — prototype VIEWS.ualerts (docs/prototype.html 4171-4179): one card per
 * client-product that is falling or on course to fall, most important client first, then the products with
 * no data yet.
 *
 * GET /usage/alerts (open + acknowledged, every page; already scoped and worst-first by `score`) grouped by
 * groupAlerts(); each card's 14-day line and "Suggested:" tip are that client-product's GET /usage/clients/{id}.
 * Forecast rules never fire from a too-uneven fit (backend, ADR §8.3), so a "Predicted" chip is a real projection.
 */
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import type { UserRef } from '@amplispot/contracts';
import { firstName } from '../../lib/format';
import { useSession } from '../../lib/session';
import { groupAlerts, useAlerts, useClientUsages, type AlertGroup } from '../../lib/usage';
import { ErrorState, Loading, priorityLabel } from '../../ui/kit';
import { ClientLogo } from '../clients/parts';
import { WhatsAppModal } from '../timesheets/TsModals';
import { daysInfo, daysLabel, Spark } from './parts';

const chipText = (a: AlertGroup) => (a.predicted ? 'Predicted' : a.sev === 'cold' ? 'Act now' : 'Watch');

export function UsageAlerts() {
  const q = useAlerts();
  const { can } = useSession();
  const nav = useNavigate();
  const mgr = can('clients.read.all');
  const [msg, setMsg] = useState<UserRef | null>(null);

  const groups = useMemo(() => groupAlerts(q.data ?? []), [q.data]);
  const real = groups.filter((g) => g.sev !== 'info');
  const stale = groups.filter((g) => g.sev === 'info');
  const usage = useClientUsages(useMemo(() => [...new Set(real.map((g) => g.client.id))], [real]));
  const open = (id: string) => nav(`/usage/client/${id}`);

  if (q.isLoading) return <Loading label="Loading alerts…" />;
  if (q.error) return <ErrorState error={q.error} onRetry={() => void q.refetch()} />;

  return (
    <>
      <div className="sub" style={{ margin: '0 0 16px', fontSize: 13.5, maxWidth: 720 }}>
        Warnings that come before a client complains: usage down on last month, or on course to fall under the low-use line within 30 days. The most important clients are first.
      </div>
      {real.length ? (
        <div className="ualgrid">
          {real.map((a) => {
            const cu = usage.get(a.client.id);
            const p = cu?.data?.products.find((x) => x.product_key === a.product_key);
            // Settled = loaded or failed: never a loading mark for good. A failed load falls back to the plain advice.
            const settled = !!cu && (!!cu.data || cu.failed);
            return (
              <div key={a.key} className={`ualrt ${a.sev}`}>
                <div className="ualh">
                  <ClientLogo id={a.client.id} name={a.client.name} size={38} />
                  <div className="grow">
                    <div className="t">
                      {a.product_name} at {a.client.name}
                    </div>
                    <div className="sub">
                      {priorityLabel(a.client.priority)} priority · SPOC {firstName(a.client.spoc.name)}
                    </div>
                  </div>
                  <span className={`chip ${a.sev}`}>{chipText(a)}</span>
                </div>
                <div className="ualb">
                  <div>
                    <p className="uwhy">{a.why}</p>
                    <p className="sub" style={{ margin: '6px 0 0' }}>
                      <b style={{ color: 'var(--ink)' }}>Suggested:</b> {p?.tip || (settled ? 'Find out why on the next call.' : '…')}
                    </p>
                  </div>
                  <div className="uspk">
                    {p && !daysInfo(p.days).drawable ? (
                      // Only today is a real daily figure: the rest would be the engine's straight-line estimate,
                      // which can rise while the alert says usage is falling. Say so instead of drawing it.
                      <div className="sub" style={{ fontSize: 11.5 }}>
                        No daily figures yet for the last 14 days.
                      </div>
                    ) : p ? (
                      <>
                        <Spark values={p.days.map((d) => d.util)} est={p.days.map((d) => d.is_estimated)} w={150} h={44} color={a.sev === 'cold' ? '#E11D48' : '#D97706'} />
                        <div className="sub" style={{ fontSize: 11.5 }}>
                          {daysLabel(p.days)}
                        </div>
                      </>
                    ) : cu?.failed ? (
                      <div className="sub" style={{ fontSize: 11.5 }}>
                        The last 14 days did not load.{' '}
                        <button type="button" className="btn sm ghost" onClick={cu.retry}>
                          Try again
                        </button>
                      </div>
                    ) : settled ? null : (
                      <span className="sub">…</span>
                    )}
                  </div>
                </div>
                <div className="ualf">
                  <button className="btn sm pri" onClick={() => open(a.client.id)}>
                    Open usage
                  </button>
                  {mgr ? (
                    <button className="btn sm" onClick={() => setMsg(a.client.spoc)}>
                      Message {firstName(a.client.spoc.name)}
                    </button>
                  ) : null}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="panel">
          <div className="empty">
            <b>No warnings</b>Every product is holding up against last month.
          </div>
        </div>
      )}
      {stale.length ? (
        <>
          <div className="lbl" style={{ margin: '26px 0 8px' }}>
            No data yet
          </div>
          <div className="panel">
            {stale.map((a) => (
              <div key={a.key} className="li click" onClick={() => open(a.client.id)}>
                <ClientLogo id={a.client.id} name={a.client.name} size={30} />
                <div className="b">
                  <div className="t">
                    {a.product_name} at {a.client.name}
                  </div>
                  <div className="m">
                    <span>Nothing uploaded yet</span>
                  </div>
                </div>
                <button className="btn sm">Open</button>
              </div>
            ))}
          </div>
        </>
      ) : null}
      {msg ? <WhatsAppModal user={msg} missingDay={null} onClose={() => setMsg(null)} /> : null}
    </>
  );
}
